'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["video", "fetchVideoAttributes", "onReplace", "onOpenVideoDetails", "onUpdateConversionAsset", "onUpdateVideoParams", "onClose"];
import FormattedMessage from 'I18n/components/FormattedMessage';
import { VideoFileIcon } from 'foundations-assets';
import { Fieldset } from 'foundations-components/transitional/components/Fieldset';
import { FileAttachment } from 'foundations-components/transitional/components/FileAttachment';
import { HR } from 'foundations-components/transitional/components/HR';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { VideoParamTypes } from '../../constants/video';
import { deepCompare, pick } from '../../utils/iterables';
import ExplicitSaveControls from '../ExplicitSaveControls';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import MorphingPopoverBody from '../MorphingPopoverBody';
import VideoPopoverAdvancedControls from './VideoPopoverAdvancedControls';
import VideoPopoverOverlayControls from './VideoPopoverOverlayControls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const renderVideoPreview = ({
  videoAttrs = {},
  onOpenVideoDetails,
  onReplace
}) => {
  const {
    name
  } = videoAttrs;
  return /*#__PURE__*/_jsxs(Fieldset, {
    _size: "small",
    paddingInline: "500",
    paddingBlockEnd: "200",
    children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
      primaryText: 'RichTextLib.video.previewLabel',
      linkText: 'RichTextLib.video.replace',
      onLinkClick: onReplace
    }), /*#__PURE__*/_jsx(FileAttachment, {
      fileName: /*#__PURE__*/_jsx("span", {
        "data-test-id": "video-details-name",
        children: name
      }),
      fileInfo: /*#__PURE__*/_jsx(Link, {
        "data-test-id": "edit-video-details-link",
        onClick: onOpenVideoDetails,
        onMouseDown: evt => evt.preventDefault(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.details'
        })
      }),
      renderPreview: /*#__PURE__*/_jsx(VideoFileIcon, {
        size: "lg"
      })
    })]
  });
};
const propTypes = Object.assign({}, VideoPopoverOverlayControls.propTypes, {
  video: PropTypes.object,
  videoName: PropTypes.string,
  fetchVideoAttributes: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onOpenVideoDetails: PropTypes.func.isRequired,
  onUpdateConversionAsset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
});
function VideoPopoverBodyEdit(props) {
  const {
      video,
      fetchVideoAttributes,
      onReplace,
      onOpenVideoDetails,
      onUpdateConversionAsset,
      onUpdateVideoParams,
      onClose
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const initialVideoParams = pick(Object.values(VideoParamTypes), video);
  const [videoParams, setVideoParams] = useState(initialVideoParams);
  const [videoAttrs, setVideoAttrs] = useState({});
  const [conversionAsset, setConversionAsset] = useState(video.conversion_asset);
  useEffect(() => {
    fetchVideoAttributes(video.player_id, {
      type: video.type
    }).then(setVideoAttrs).catch(() => setVideoAttrs({}));
  }, [fetchVideoAttributes, video]);
  const handleOpenVideoDetails = () => {
    onOpenVideoDetails(video, updatedVideo => {
      setVideoAttrs({
        name: updatedVideo.name
      });
    });
  };
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleSubmit = () => {
    if (!deepCompare(conversionAsset, video.conversion_asset)) {
      onUpdateConversionAsset(conversionAsset);
    }
    if (!deepCompare(videoParams, initialVideoParams)) {
      onUpdateVideoParams(videoParams);
    }
    onClose();
  };
  const isSubmitDisabled = conversionAsset && conversionAsset.type && !conversionAsset.id;
  return /*#__PURE__*/_jsx(View, {
    "data-test-id": "video-popover-body-edit",
    inlineSize: "294px",
    tabIndex: "-1",
    children: /*#__PURE__*/_jsx(MorphingPopoverBody, {
      title: 'RichTextLib.video.popoverTitle',
      paddingInline: "500",
      paddingBlockStart: "500",
      morphButtonTitle: 'RichTextLib.video.advanced.title',
      morphedContentTitle: 'RichTextLib.video.advanced.title',
      renderMorphedContent: () => /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(VideoPopoverAdvancedControls, {
          videoParams: videoParams,
          onVideoParamChange: setVideoParams
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          paddingBlockStart: "300",
          paddingBlockEnd: "500",
          paddingInline: "500",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      }),
      children: /*#__PURE__*/_jsxs("div", {
        onKeyDown: handleCloseOnEscape,
        children: [/*#__PURE__*/_jsx(VideoPopoverOverlayControls, Object.assign({}, rest, {
          conversionAsset: conversionAsset,
          video: videoAttrs,
          onUpdateConversionAsset: setConversionAsset
        })), /*#__PURE__*/_jsx(HR, {
          distance: "xs"
        }), renderVideoPreview({
          videoAttrs,
          onReplace,
          onOpenVideoDetails: handleOpenVideoDetails
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          paddingBlockStart: "300",
          paddingBlockEnd: "500",
          paddingInline: "500",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      })
    })
  });
}
VideoPopoverBodyEdit.propTypes = propTypes;
VideoPopoverBodyEdit.displayName = 'VideoPopoverBodyEdit';
export default VideoPopoverBodyEdit;